import { Unstable_Popup as BasePopup } from '@mui/base/Unstable_Popup';
import { ClickAwayListener } from '@mui/base/ClickAwayListener';
import { styled } from '@mui/system';
import { useState, MouseEvent } from 'react';
import {
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  ListItemAvatar,
  ListItemIcon,
  Typography,
  Divider,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import {
  Logout as LogoutIcon,
  Settings as SettingsIcon,
  LightModeOutlined as LightModeOutlinedIcon,
  DarkModeOutlined as DarkModeOutlinedIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { Navigation } from './i18n';
import { toggleButtonGroupClasses } from '@mui/material/ToggleButtonGroup';
import { useThemeStore } from '@diana/core';
import { ThemeNames } from '@diana/mui';
import { SettingsModal } from '../Modals/SettingsModal';
import { useSignOut } from '../../hooks/useSignOut';
import { UserAvatar } from '../UserAvatar/UserAvatar';
import { useUserData } from '../../hooks/auths/useUserData';

export const PopupSettingsMenu = () => {
  const { name, email, lastName } = useUserData();
  const { t } = useTranslation(Navigation);
  const [anchor, setAnchor] = useState<null | HTMLElement>(null);
  const themeName = useThemeStore((store) => store.themeName);
  const setTheme = useThemeStore((state) => state.setTheme);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const handleSignOut = useSignOut();

  const handleFormat = (event: MouseEvent<HTMLElement>, newThemes: any) => {
    setTheme(newThemes.filter((theme: ThemeNames) => themeName !== theme)[0]);
  };

  const handleClick = (event: MouseEvent<HTMLElement>) => {
    setAnchor(anchor ? null : event.currentTarget);
  };

  const handleOutsideClick = () => {
    setAnchor(null);
  };

  const handleSettingsClick = () => {
    setShowSettingsModal((prevState) => !prevState);
  };

  const open = Boolean(anchor);
  const id = open ? 'simple-popup' : undefined;

  return (
    <ClickAwayListener onClickAway={handleOutsideClick}>
      <div>
        <UserAvatar size={32} onClick={handleClick} name={name} lastName={lastName} />
        <BasePopup id={id} open={open} anchor={anchor}>
          <PopupBody>
            <List sx={{ width: '100%', padding: 0 }}>
              {/* User Info */}
              <ListItem sx={{ p: '4px 6px' }}>
                <ListItemAvatar sx={{ minWidth: 'auto' }}>
                  <UserAvatar size={24} name={name} lastName={lastName} />
                </ListItemAvatar>
                <ListItemText
                  sx={{ ml: '6px' }}
                  primary={`${name} ${lastName}`}
                  secondary={email}
                />
              </ListItem>
              <Divider />
              {/* SettingsModal, theme switcher */}
              <ListItemButton
                sx={{ pl: '12px' }}
                alignItems="flex-start"
                onClick={handleSettingsClick}
              >
                <ListItemIcon sx={{ minWidth: 'auto' }} aria-label="fingerprint" color="secondary">
                  <SettingsIcon sx={{ width: 14, height: 14 }} />
                </ListItemIcon>
                <ListItemText sx={{ ml: '6px' }} primary={t('menuList.settings')} />
              </ListItemButton>
              <Typography sx={{ p: '2px 6px' }}>{t('menuList.themeSwitcher.title')}</Typography>
              <StyledToggleButtonGroup
                sx={{ mt: '10px', ml: '5px', display: 'flex' }}
                size="small"
                value={[themeName]}
                onChange={handleFormat}
                aria-label="text formatting"
              >
                <ToggleButton
                  sx={{
                    padding: 0,
                    flex: 1,
                    '&.Mui-selected': {
                      backgroundColor: (theme) =>
                        theme.palette.mode === 'dark' ? theme.palette.background.paper : 'white',
                      border: (theme) =>
                        `1px solid ${theme.palette.mode === 'dark' ? theme.palette.divider : '#E0E0E0'}`,
                      '&:hover': {
                        backgroundColor: (theme) =>
                          theme.palette.mode === 'dark' ? theme.palette.background.paper : 'white',
                      },
                    },
                    '&:not(.Mui-selected)': {
                      backgroundColor: 'transparent',
                      border: 'none',
                      '&:hover': {
                        backgroundColor: (theme) =>
                          theme.palette.mode === 'dark'
                            ? 'rgba(255, 255, 255, 0.08)'
                            : 'rgba(0, 0, 0, 0.04)',
                      },
                    },
                  }}
                  value="dianaLight"
                  aria-label="light theme"
                >
                  <LightModeOutlinedIcon sx={{ fontSize: 16, mr: 0.5, color: 'primary.main' }} />
                  {t('menuList.themeSwitcher.light')}
                </ToggleButton>
                <ToggleButton
                  sx={{
                    padding: 0,
                    flex: 1,
                    '&.Mui-selected': {
                      backgroundColor: (theme) =>
                        theme.palette.mode === 'dark' ? theme.palette.background.paper : 'white',
                      border: (theme) =>
                        `1px solid ${theme.palette.mode === 'dark' ? theme.palette.divider : '#E0E0E0'}`,
                      '&:hover': {
                        backgroundColor: (theme) =>
                          theme.palette.mode === 'dark' ? theme.palette.background.paper : 'white',
                      },
                    },
                    '&:not(.Mui-selected)': {
                      backgroundColor: 'transparent',
                      border: 'none',
                      '&:hover': {
                        backgroundColor: (theme) =>
                          theme.palette.mode === 'dark'
                            ? 'rgba(255, 255, 255, 0.08)'
                            : 'rgba(0, 0, 0, 0.04)',
                      },
                    },
                  }}
                  value="dianaDark"
                  aria-label="dark theme"
                >
                  <DarkModeOutlinedIcon sx={{ fontSize: 16, mr: 0.5, color: 'primary.main' }} />
                  {t('menuList.themeSwitcher.dark')}
                </ToggleButton>
              </StyledToggleButtonGroup>
              <Divider sx={{ mt: '12px' }} />
              {/* Sign out */}
              <ListItemButton sx={{ pl: '12px' }} alignItems="flex-start" onClick={handleSignOut}>
                <ListItemIcon sx={{ minWidth: 'auto' }} aria-label="fingerprint" color="secondary">
                  <LogoutIcon sx={{ width: 14, height: 14 }} />
                </ListItemIcon>
                <ListItemText sx={{ ml: '6px' }} primary={t('menuList.signOut')} />
              </ListItemButton>
            </List>
          </PopupBody>
        </BasePopup>
        <SettingsModal open={showSettingsModal} onClose={handleSettingsClick} />
      </div>
    </ClickAwayListener>
  );
};

const grey = {
  200: '#DAE2ED',
  700: '#434D5B',
  900: '#1C2025',
};

const StyledToggleButtonGroup = styled(ToggleButtonGroup)(({ theme }) => ({
  backgroundColor: theme.palette.divider,
  [`& .${toggleButtonGroupClasses.grouped}`]: {
    margin: theme.spacing(0.5),
    border: 0,
    borderRadius: theme.shape.borderRadius,
    [`&.${toggleButtonGroupClasses.disabled}`]: {
      border: 0,
    },
  },
  [`& .${toggleButtonGroupClasses.middleButton},& .${toggleButtonGroupClasses.lastButton}`]: {
    marginLeft: -1,
    borderLeft: '1px solid transparent',
  },
}));

const PopupBody = styled('div')(
  ({ theme }) => `
  width: 300px;
  padding: 8px;
  margin: 8px;
  border-radius: 8px;
  border: 1px solid ${theme.palette.mode === 'dark' ? grey[700] : grey[200]};
  background-color: ${theme.palette.mode === 'dark' ? grey[900] : '#fff'};
  box-shadow: ${
    theme.palette.mode === 'dark' ? `0px 4px 8px rgb(0 0 0 / 0.7)` : `0px 4px 8px rgb(0 0 0 / 0.1)`
  };
  font-weight: 500;
  font-size: 0.875rem;
  z-index: 1;
`,
);
